// PNY-CART-REWARDS
// Keeps the free gift line in sync with the tiered rewards bar rendered by snippets/free-shipping-bar.liquid.
// All the state (eligibility, gift line key...) is computed in Liquid and passed as attributes; this element only
// adds the gift when the cart becomes eligible, and removes the auto-added gift when it no longer is.
//
// The element is re-created every time the cart drawer re-renders (on every cart change), so the sync naturally
// runs again after each change. A global lock prevents the cart page and the cart drawer (both rendering the bar)
// from racing each other.

const GIFT_PROPERTY = '_pny_gift';
let isSyncing = false;
let hasFailed = false;

class CartRewards extends HTMLElement {
  connectedCallback() {
    // Only ever change the cart while the store is on screen. A background tab (for instance while the customer is on
    // the checkout page in another tab) must never add or remove the gift: the checkout page would keep showing the
    // old state while the order is created from the changed cart. When the tab becomes visible again, the cart is
    // re-fetched first (cart:refresh re-renders the drawer, which re-creates this element with fresh data).
    if (document.visibilityState !== 'visible') {
      const onVisible = () => {
        if (document.visibilityState !== 'visible') return;

        document.removeEventListener('visibilitychange', onVisible);

        if (this.isConnected) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      };

      document.addEventListener('visibilitychange', onVisible);
      return;
    }

    // Let the rest of the section finish connecting before touching the cart
    requestAnimationFrame(() => this.#sync());
  }

  get #giftVariantId() {
    return this.getAttribute('gift-variant-id');
  }

  get #autoGiftKey() {
    return this.getAttribute('auto-gift-key');
  }

  async #sync() {
    const state = {
      eligible: this.hasAttribute('gift-eligible'),
      giftVariantId: this.#giftVariantId,
      autoGiftKey: this.#autoGiftKey,
      giftInCart: this.hasAttribute('gift-in-cart'),
      isSyncing,
      hasFailed,
      designMode: Boolean(window.Shopify?.designMode)
    };

    console.debug('[PNY cart rewards] state', state);

    if (isSyncing || hasFailed || !this.isConnected || document.visibilityState !== 'visible') {
      return;
    }

    if (state.designMode) {
      console.info('[PNY cart rewards] Free gift is not auto-added inside the theme editor. Use the theme preview link to test.');
      return;
    }

    if (state.eligible && !state.giftVariantId) {
      console.warn('[PNY cart rewards] Eligible, but no gift variant: check that the gift product is set in Theme settings > Cart, in stock and published on the Online Store.');
    }

    if (state.eligible && state.giftVariantId && !state.autoGiftKey && !state.giftInCart) {
      await this.#run(() => this.#addGift());
    } else if (!state.eligible && state.autoGiftKey) {
      await this.#run(() => this.#removeGift());
    }
  }

  async #run(action) {
    isSyncing = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      await action();
    } catch (error) {
      hasFailed = true; // Don't retry in a loop (for instance if the gift is out of stock)
      console.warn('[PNY cart rewards] Unable to update the free gift', error);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      isSyncing = false;
    }
  }

  async #addGift() {
    console.info('[PNY cart rewards] Adding free gift', this.#giftVariantId);

    await this.#updateCart(`${Shopify.routes.root}cart/add.js`, {
      items: [{ id: Number(this.#giftVariantId), quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }]
    });
  }

  async #removeGift() {
    console.info('[PNY cart rewards] Removing free gift', this.#autoGiftKey);

    await this.#updateCart(`${Shopify.routes.root}cart/change.js`, { id: this.#autoGiftKey, quantity: 0 });
  }

  // Performs the request with the bundled sections, and notifies the theme so the cart drawer / cart count re-render
  // (same flow as the theme's own product form: request with "sections", then fetch the full cart.js)
  async #updateCart(url, body) {
    const isCartPage = window.themeVariables?.settings?.pageType === 'cart';
    const sections = [];

    if (!isCartPage) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(sections.length > 0 ? { ...body, sections: sections.join(',') } : body)
    });

    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson['description'] || responseJson['message'] || response.status);
    }

    if (isCartPage) {
      // Safety net against a reload loop if the cart does not end up in the expected state
      try {
        if (Date.now() - Number(sessionStorage.getItem('pny-cart-rewards-reload') || 0) < 5000) return;
        sessionStorage.setItem('pny-cart-rewards-reload', String(Date.now()));
      } catch (error) {}

      window.location.reload();
      return;
    }

    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cart['sections'] = responseJson['sections'];

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'pny:cart-rewards', cart }
    }));
  }
}

if (!window.customElements.get('cart-rewards')) {
  window.customElements.define('cart-rewards', CartRewards);
}
